import { MaybeObservable, unwrap } from "knockout";


// Test for an empty (or whitespace) string
const emptyTestRegex = new RegExp("^\\s*$");

// Regular expression to match regular expression special characters for escaping in toRegExLiteral
const toLiteralRegex = new RegExp("[\\\\\\[\\]\\^\\{\\}\\<\\>\\-\\$\\.\\?\\*\\(\\)\\|\\+]", "g");

/**
 * Builds a regex that prefix-matches all phrases of a search string.
 *
 * @param search
 *     String containing the words that will be prefix-matched.
 * @returns
 *     Regular expression that prefix-matches all phrases in the search string.
 */
export function buildPrefixMatcher(search: string): RegExp {
    const phrases = (search || "").split(" ");
    let reconstructed = "";

    for (const phrase of phrases) {
        const p = phrase.trim();
        if (p.length !== 0) {
            reconstructed += `(?=.*\\b${toRegExLiteral(p)})`;
        }
    }

    return new RegExp(reconstructed, "i");
}

/**
 * Create regular expression that tests for strings starting with the specified value, case-insensitive.
 *
 * @param prefix
 *     String to match.
 * @returns
 *     Regular expression instance.
 */
export function createSearchRegexFor(prefix: string): RegExp {
    return new RegExp("^" + toRegExLiteral(prefix), "i"); // case insensitive
}

/**
 * Determines whether the specified value is an "empty" string.
 *
 * "null", "undefined", "empty" and "whitespace" are all considered empty.
 *
 * @param value
 *     Value to check.
 * @returns
 *     true/false if passed value is an empty string. Non-string values are
 *     considered to be "empty" strings and return true.
 */
export function isNullOrWhitespace(value: string): boolean {
    return (typeof value !== "string" || emptyTestRegex.test(value));
}

/**
 * Converts string value to a boolean.
 *
 * Mimics "BoolType.TryParseExact" behavior.
 *
 * @param value
 *     Value to parse as a boolean.
 * @returns
 *     true/false if passed value is a string that can be parsed, "null" otherwise.
 */
export function parseBoolean(value: string): boolean {
    if (typeof value !== "string") {
        return null;
    }

    switch (value.toUpperCase()) {
        case "-1":
        case "1":
        case "Y":
        case "YES":
        case "T":
        case "TRUE":
        case "ON":
        case "OK":
            return true;

        case "0":
        case "N":
        case "NO":
        case "F":
        case "FALSE":
            return false;

        default:
            return null;
    }
}

/**
 * Escape special regex characters so that they are matched literally.
 *
 * (E.g. a string containing "." will match dot, not any character, at that position).
 *
 * @param value
 *     String to convert to literal regex.
 * @returns
 *     A string that can be used in a RegExp object for matching literal values.
 */
export function toRegExLiteral(value: string): string {
    return value.toString().replace(toLiteralRegex, c => "\\" + c);
}

/**
 * Removes leading and trailing whitespace from the given string.
 *
 * Code is modified from https://github.com/knockout/knockout/blob/master/src/utils.js.
 *
 * @param value
 *     The string or observable to trim.
 * @returns
 *     The trimmed string or an empty string if input is null or undefined.
 */
export function trim(value: MaybeObservable<string>): string {
    const strValue = unwrap(value);

    return (strValue === null || strValue === undefined)
        ? ""
        : (strValue.trim ? strValue.trim() : strValue.toString().replace(/^[\s\xa0]+|[\s\xa0]+$/g, ""));
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/util/string.ts